import clsx from "clsx";
import { useState, useRef, FC } from "react";
import { Slide } from "react-slideshow-image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid, IconButton, useMediaQuery } from "@mui/material";
import { CMSImageType } from "@Types/Contentful";
import Image from "components/Image";
import ProductZoomInModal from "./ProductZoomInModal";
import classes from "./ProductGallery.module.scss";
interface ISlide extends Slide {}

interface IProductGalleryProps {
  imageSet: {
    productId?: string;
    mainImage: CMSImageType;
    otherImages?: {
      items: CMSImageType[];
    };
    nutritionFactsImage?: CMSImageType;
  };
}

interface ISliderMenuProps {
  mainImage: CMSImageType;
  otherImages?: {
    items: CMSImageType[];
  };
  nutritionFactsImage?: CMSImageType;
}

const ProductGallery: FC<IProductGalleryProps> = ({ imageSet }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const sliderRef = useRef<ISlide>(null);
  const [image, setImage] = useState("");
  const containerRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:480px)");
  const { mainImage, otherImages, nutritionFactsImage } = imageSet;
  const nutritionImageIndex = otherImages?.items
    ? otherImages?.items?.length + 1
    : 1;
  if (!mainImage?.url) {
    return null;
  }

  const onChangeHandler = (_: never, newIndex: number) => {
    setSelectedImage(newIndex);
  };

  const isSelected = (index: number) => {
    return index === selectedImage;
  };

  const onThumbnailClick = (index: number) => {
    if (sliderRef?.current?.goTo) {
      sliderRef.current.goTo(index);
      setSelectedImage(index);
    }
  };

  const handleZoomIn = (image: any) => {
    setImage(image);
  };

  const scrollToBottom = () => {
    if (containerRef.current) {
      const child = containerRef.current as any;
      child.scrollTo({
        top: child.offsetHeight,
        behavior: "smooth",
      });
    }
  };

  const countImages = () => {
    let count = 0;
    return function (image: any) {
      if (image) {
        return (count += Object.entries(image).length > 0 ? 1 : 0);
      }
      return count;
    };
  };

  const SliderMenu: FC<ISliderMenuProps> = ({
    mainImage,
    otherImages,
    nutritionFactsImage,
  }) => {
    let imagesLength = 0;
    const imageCountSum = countImages();
    imageCountSum(mainImage);
    imagesLength =
      (otherImages &&
        otherImages?.items?.length + imageCountSum(nutritionFactsImage)) ||
      0;

    const showExpandMore = imagesLength > 4;
    return (
      <Grid
        item
        className={`${classes["thumbnail-slider"]}`}
        ref={containerRef}
      >
        <div>
          <div
            className={clsx(
              classes["thumbnail-slider__wrapper"],
              isSelected(0) && classes["selected"]
            )}
            key={0}
            onClick={() => onThumbnailClick(0)}
            onKeyDown={() => onThumbnailClick(nutritionImageIndex)}
          >
            <Image
              src={`${mainImage?.url}?fm=webp&w=1024&h=768`}
              alt={mainImage?.title}
              height={100}
              width={100}
              layout="responsive"
            />
          </div>
          {otherImages?.items.map((image, index) => (
            <div
              className={clsx(
                classes["thumbnail-slider__wrapper"],
                isSelected(index + 1) && classes["selected"]
              )}
              key={index + 1}
              onClick={() => onThumbnailClick(index + 1)}
              onKeyDown={() => onThumbnailClick(nutritionImageIndex)} //added to remove sonar lint warning
            >
              <Image
                src={`${image?.url}?fm=webp&w=1024&h=768`}
                alt={image.title}
                height={100}
                width={100}
                layout="responsive"
              />
            </div>
          ))}
          <div
            className={clsx(
              classes["thumbnail-slider__wrapper"],
              isSelected(nutritionImageIndex) && classes["selected"]
            )}
            key={nutritionImageIndex}
            onClick={() => onThumbnailClick(nutritionImageIndex)}
            onKeyDown={() => onThumbnailClick(nutritionImageIndex)}
          >
            {nutritionFactsImage?.url && (
              <Image
                src={`${nutritionFactsImage?.url}?fm=webp&w=1024&h=768`}
                alt={nutritionFactsImage?.title}
                height={100}
                width={100}
                layout="responsive"
              />
            )}
          </div>
          {showExpandMore && (
            <div className={classes["show-more"]}>
              <IconButton onClick={scrollToBottom}>
                <ExpandMoreIcon strokeWidth={1.5} fontSize="large" />
              </IconButton>
            </div>
          )}
        </div>
      </Grid>
    );
  };
  return (
    <Grid
      container
      className={classes["product-gallery"]}
      data-testid="product-gallery-container"
    >
      <SliderMenu
        mainImage={mainImage}
        otherImages={otherImages}
        nutritionFactsImage={nutritionFactsImage}
      />
      <Grid item className={classes["slider"]}>
        <Slide
          autoplay={false}
          infinite={false}
          onChange={onChangeHandler}
          ref={sliderRef}
          transitionDuration={500}
        >
          <div style={{ position: "relative" }}>
            <Image
              key={0}
              src={`${mainImage?.url}?fm=webp&w=1920&h=1080`}
              alt={mainImage?.title}
              height={100}
              width={100}
              layout="responsive"
            />
            {!isMobile && (
              <ProductZoomInModal
                handleZoomIn={() => handleZoomIn(mainImage)}
                image={image}
              />
            )}
          </div>
          {otherImages?.items.map((image) => (
            <div style={{ position: "relative" }} key={image.title}>
              <Image
                alt={image.title}
                height={600}
                layout="responsive"
                src={`${image?.url}?fm=webp&w=1920&h=1080`}
                width={600}
              />
              {!isMobile && (
                <ProductZoomInModal
                  handleZoomIn={() => handleZoomIn(image)}
                  image={image}
                />
              )}
            </div>
          ))}
          {nutritionFactsImage && (
            <div style={{ position: "relative" }}>
              <Image
                key={nutritionImageIndex}
                src={`${nutritionFactsImage?.url}?fm=webp&w=1920&h=1080`}
                alt={nutritionFactsImage?.title}
                height={100}
                width={100}
                layout="responsive"
              />
              {!isMobile && (
                <ProductZoomInModal
                  handleZoomIn={() => handleZoomIn(nutritionFactsImage)}
                  image={image}
                />
              )}
            </div>
          )}
        </Slide>
      </Grid>
    </Grid>
  );
};

export default ProductGallery;
